/**
 * One second-factor step, the same for a step-up on the account page and the
 * second factor at login. The only difference is which challenge endpoint
 * runs, so the caller passes it in.
 */

import type { ChallengeResponse, LoginMfaMethod, StepUpProof, WebAuthnJson } from '@dealr/core-account/domain';
import { assertionToJson, normalizeBackupCode, normalizeCode, requestOptionsFromJson, stepUpKind } from '@dealr/core-account';
import type { WebAuthnPort } from './ports';

export interface SecondFactorDeps {
  challenge(methodUuid: string): PromiseLike<ChallengeResponse>;
  webauthn: WebAuthnPort | null;
}

/** Either a code is needed (`sent` says whether one was just texted/called), or the proof is ready. */
export type SecondFactorStart =
  | { needsCode: true; sent: boolean }
  | { needsCode: false; proof: StepUpProof };

export class SecondFactorError extends Error {
  constructor(
    message: string,
    readonly cancelled = false,
  ) {
    super(message);
    this.name = 'SecondFactorError';
  }
}

/**
 * Authenticator app: nothing to send, ask for the code. SMS/voice: the
 * challenge sends the code. Security key: the challenge returns request
 * options and the browser signs, so the proof is ready without typing.
 */
export async function startSecondFactor(method: Pick<LoginMfaMethod, 'uuid' | 'type'>, deps: SecondFactorDeps): Promise<SecondFactorStart> {
  const kind = stepUpKind(method.type);
  if (kind === 'code') return { needsCode: true, sent: false };
  const response = await deps.challenge(method.uuid);
  if (kind === 'send-code') return { needsCode: true, sent: true };
  if (!response || !response.publicKey) throw new SecondFactorError('The server did not return security key options.');
  const assertion = await signAssertion(response.publicKey, deps.webauthn);
  return { needsCode: false, proof: { method_uuid: method.uuid, assertion } };
}

export function codeProof(method: Pick<LoginMfaMethod, 'uuid'> & { type?: string }, code: string): StepUpProof {
  return { method_uuid: method.uuid, code: method.type === 'backup_code' ? normalizeBackupCode(code) : normalizeCode(code) };
}

export async function signAssertion(publicKey: WebAuthnJson, webauthn: WebAuthnPort | null): Promise<WebAuthnJson> {
  if (!webauthn) throw new SecondFactorError('This browser does not support security keys or passkeys.');
  let credential;
  try {
    credential = await webauthn.get(requestOptionsFromJson(publicKey));
  } catch (error) {
    throw cancelOr(error);
  }
  if (!credential) throw new SecondFactorError('No security key response was received.', true);
  return assertionToJson(credential);
}

export function cancelOr(error: unknown): SecondFactorError {
  const name = error && typeof error === 'object' ? (error as { name?: unknown }).name : undefined;
  if (name === 'NotAllowedError' || name === 'AbortError') {
    return new SecondFactorError('The security key request was cancelled or timed out.', true);
  }
  if (name === 'InvalidStateError') {
    return new SecondFactorError('This security key is already registered.');
  }
  return new SecondFactorError(error instanceof Error && error.message ? error.message : 'The security key request failed.');
}
